import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { $getRoot, $createParagraphNode, $createTextNode, TextNode } from 'lexical'
import { SelectNode } from './chatting-structured/nodes/selectNode'
import { InputNode } from './chatting-structured/nodes/inputNode'
import { InsertSelectPlugin } from './chatting-structured/plugins/InsertSelectPlugin'
import { InsertInputPlugin } from './chatting-structured/plugins/InsertInputPlugin'
import { InsertTextPlugin } from './chatting-structured/plugins/InsertTextPlugin'
import { PlaceholderPlugin } from './chatting-structured/plugins/PlaceholderPlugin'
import { MaxLengthPlugin } from './chatting-structured/plugins/MaxLengthPlugin'
import { generateInsertFunction, extractText } from './chatting-structured/utils'

export { generateInsertFunction }

const MAX_LENGTH = 1000

function $getOrCreateParagraph() {
  const root = $getRoot()
  const last = root.getLastChild()
  if (last && last.getType() === 'paragraph') return last
  const para = $createParagraphNode()
  root.append(para)
  return para
}

function EditorFocusArea({ children }) {
  const [editor] = useLexicalComposerContext()
  const handleClick = e => {
    const root = editor.getRootElement()
    if (root && !root.contains(e.target)) root.focus()
  }
  return (
    <div className="fdai-structured-focus-area" onClick={handleClick}>
      {children}
    </div>
  )
}

const StructuredDescEditor = forwardRef(({ onChange, placeholder }, ref) => {
  const insertRef = useRef({
    insertSelect: () => {},
    insertInput: () => {},
    insertText: () => {},
    editor: null,
  })

  useImperativeHandle(ref, () => ({
    insertSelect: props => insertRef.current.insertSelect(props),
    insertInput: props => insertRef.current.insertInput(props),
    insertText: text => insertRef.current.insertText(text),
    getContent: () => {
      const { editor } = insertRef.current
      if (!editor) return ''
      let text = ''
      editor.getEditorState().read(() => { text = extractText($getRoot()) })
      return text
    },
    clearContent: () => {
      const { editor } = insertRef.current
      if (!editor) return
      editor.update(() => {
        const root = $getRoot()
        root.clear()
        const para = $createParagraphNode()
        root.append(para)
        para.select()
      })
    },
    setContent: content => {
      const { editor } = insertRef.current
      if (!editor) return
      editor.update(() => {
        const root = $getRoot()
        root.clear()
        const para = $createParagraphNode()
        if (content) para.append($createTextNode(content))
        root.append(para)
        para.selectEnd()
      })
    },
    getEditorState: () => {
      const { editor } = insertRef.current
      if (!editor) return null
      return JSON.stringify(editor.getEditorState().toJSON())
    },
    restoreEditorState: json => {
      const { editor } = insertRef.current
      if (!editor || !json) return
      try {
        const state = editor.parseEditorState(json)
        // 用 queueMicrotask 推迟到 React commit 完成后再调用，
        // 避免 setEditorState 内部的 flushSync 在 lifecycle 中触发警告
        queueMicrotask(() => {
          try { editor.setEditorState(state) } catch (e) { console.warn('restoreEditorState error', e) }
        })
      } catch (e) { console.warn('restoreEditorState error', e) }
    },
  }))

  return (
    <LexicalComposer initialConfig={{
      namespace: 'fdai-structured',
      onError: console.error,
      nodes: [SelectNode, InputNode],
    }}
    >
      <EditorFocusArea>
        <div className="fdai-structured-inner">
          {placeholder && <PlaceholderPlugin placeholder={placeholder} />}
          <RichTextPlugin
            contentEditable={<ContentEditable className="fdai-structured-content-editable" />}
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <MaxLengthPlugin maxLength={MAX_LENGTH} />
        {onChange && (
          <OnChangePlugin onChange={editorState => {
            editorState.read(() => { onChange(extractText($getRoot())) })
          }}
          />
        )}
        <InsertSelectPlugin insertRef={insertRef} />
        <InsertInputPlugin insertRef={insertRef} />
        <InsertTextPlugin insertRef={insertRef} />
      </EditorFocusArea>
    </LexicalComposer>
  )
})

StructuredDescEditor.displayName = 'StructuredDescEditor'
export default StructuredDescEditor

